import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  popupNotificationsQueryKey,
  unreadNotificationsCountQueryKey,
} from "@/lib/constants";
import { guacHttpClient } from "@/lib/http";
import {
  DeleteManyResponse,
  HttpResponse,
  NotificationResponse,
  UpdateNotificationsRequest,
} from "@/types";

export const useUpdateSetNotifications = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: UpdateNotificationsRequest) => {
      const res = await guacHttpClient.patch<
        DeleteManyResponse,
        HttpResponse<DeleteManyResponse>,
        UpdateNotificationsRequest
      >("notification-recipients", request);
      return res.data;
    },
    onSuccess: (_, { dismiss, markAsRead, notificationIds }) => {
      const updatedIds = new Set(notificationIds);
      const now = new Date().toISOString();

      // Mirrors AdminNotificationRecipientsService.updateManyForUser's
      // precedence exactly
      let dismissedAt: string | null | undefined;
      if (markAsRead || dismiss) {
        dismissedAt = now;
      }

      let readAt: string | null | undefined;
      if (markAsRead !== undefined) {
        readAt = markAsRead ? now : null;
      }

      if (readAt !== undefined || dismissedAt !== undefined) {
        if (readAt !== undefined) {
          void queryClient.invalidateQueries({
            queryKey: unreadNotificationsCountQueryKey,
          });
        }

        queryClient.setQueryData<NotificationResponse[]>(
          ["notifications"],
          (cachedData) =>
            cachedData?.map((notification) => {
              if (!updatedIds.has(notification.id)) return notification;
              return {
                ...notification,
                readAt: readAt === undefined ? notification.readAt : readAt,
                dismissedAt:
                  dismissedAt === undefined
                    ? notification.dismissedAt
                    : dismissedAt,
              };
            }),
        );
      }

      // Marking read or dismissing closes the popup
      if (markAsRead || dismiss) {
        queryClient.setQueryData<NotificationResponse[]>(
          popupNotificationsQueryKey,
          (cachedData) =>
            cachedData?.filter(
              (notification) => !updatedIds.has(notification.id),
            ),
        );
      }
    },
  });
};
